import PropTypes from "prop-types";
import MDBox from "theme/components/MDBox";
import MDInput from "theme/components/MDInput";
import { useState, useEffect } from "react";
import { Snackbar, Alert, Icon, ListItem, ListItemText } from "@mui/material";
import MDButton from "theme/components/MDButton";
import MDTypography from "theme/components/MDTypography";

function DecimalField({ formRef, draftIndex, displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errMsg, setErrMsg] = useState("Invalid entry provided.");

  const [number, setNumber] = useState(null);
  const [formattedNumber, setFormattedNumber] = useState(null);

  const handleInputChange = (e) => {
    const inputVal = e.target.value;
    const numberVal = inputVal.replace(/,/g, "");
    if (numberVal.length === 0) {
      setFormattedNumber("");
      return;
    }

    if (numberVal.endsWith(".")) {
      setFormattedNumber(numberVal);
      return;
    }

    const isnum = /^[0-9]\d*(\.\d+)?$/.test(numberVal);
    if (!isnum && !numberVal.endsWith(".")) {
      alert("No letters allowed.");
      setNumber(null);
      return;
    }

    // const x = parseFloat(numberVal);
    const fNum = parseFloat(numberVal, 10).toLocaleString("en", {
      // minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
    setFormattedNumber(fNum);
  };

  useEffect(() => {
    if (number === null) {
      return;
    }

    const watchField = document.getElementById(`dff_${fieldRef}`);
    watchField.dispatchEvent(new Event("change"));
  }, [number]);

  const [hasSavedValue, setHasSavedValue] = useState(false);
  const [draftValue, setDraftValue] = useState(null);

  useEffect(() => {
    if (!formRef) return;

    const saved = localStorage.getItem(`draft_${formRef}`);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const val = parsed?.[draftIndex].values?.[fieldRef];

      if (val !== undefined && val !== null && val !== "") {
        setDraftValue(val);
        setHasSavedValue(true);
      }
    } catch (e) {
      console.error("Draft parse error", e);
    }
  }, [formRef, fieldRef]);

  return (
    <MDBox>
      {hasSavedValue ? (
        <MDBox>
          <ListItem divider>
            <MDBox mr={1}>
              <Icon color="success">check_circle</Icon>
            </MDBox>
            <ListItemText
              primary={
                <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                  {displayName}
                </MDTypography>
              }
              secondary={
                <MDTypography variant="body2" fontSize={13}>
                  {draftValue}
                </MDTypography>
              }
            />
            <MDButton
              size="small"
              color="warning"
              variant="outlined"
              iconOnly
              onClick={() => {
                try {
                  const key = `draft_${formRef}`;
                  const saved = localStorage.getItem(key);
                  if (!saved) return;

                  const parsed = JSON.parse(saved);

                  if (parsed?.values && fieldRef in parsed.values) {
                    delete parsed.values[fieldRef];
                  }

                  // persist updated draft
                  localStorage.setItem(key, JSON.stringify(parsed));

                  // update UI state
                  setHasSavedValue(false);
                  setDraftValue(null);
                } catch (e) {
                  console.error("Failed to clear draft field", e);
                }
              }}
            >
              <Icon>edit</Icon>
            </MDButton>
          </ListItem>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={draftValue}
          />
        </MDBox>
      ) : (
        <MDBox>
          <MDInput
            margin="dense"
            label={displayName}
            value={formattedNumber}
            type="text"
            fullWidth
            variant="outlined"
            onChange={handleInputChange}
            onBlur={(e) => {
              // const entryValue = e.target.value;
              const inputVal = e.target.value;
              const entryValue = inputVal.replace(/,/g, "");

              const isnum = /^[0-9]\d*(\.\d+)?$/.test(entryValue);
              if (!isnum && !entryValue.endsWith(".")) {
                e.target.value = null;
                setNumber(null);
                setErrMsg(`${displayName} requires only valid amounts.`);
                showFieldError(true);
                return;
              }

              const numValue = parseFloat(entryValue);
              if (typeof options.min_number !== "undefined") {
                if (numValue < options.min_number) {
                  e.target.value = null;
                  setNumber(null);
                  setErrMsg(
                    `${displayName} cannot be less than ${options.min_number}`,
                  );
                  showFieldError(true);
                  return;
                }
              }

              if (typeof options.max_number !== "undefined") {
                if (numValue > options.max_number) {
                  e.target.value = null;
                  setNumber(null);
                  setErrMsg(
                    `${displayName} cannot be more than ${options.max_number}`,
                  );
                  showFieldError(true);
                  return;
                }
              }

              setNumber(numValue);
            }}
            options={options}
          />
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={5000}
            onClose={() => showFieldError(false)}
            open={fieldError}
          >
            <Alert severity="error" sx={{ width: "100%" }}>
              {errMsg}
            </Alert>
          </Snackbar>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={number}
          />
        </MDBox>
      )}
    </MDBox>
  );
}

// Typechecking props of the Transaction
DecimalField.propTypes = {
  formRef: PropTypes.string.isRequired,
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

DecimalField.defaultProps = {
  options: null,
};

export default DecimalField;
